import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import StatusBadge from "../components/StatusBadge";
import LiveMap from "../components/LiveMap";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  Users, CheckCircle, Warning, XCircle, ClockClockwise, ChartPieSlice,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmt(s) {
  return s ? new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayRecords, setTodayRecords] = useState([]);
  const [stores, setStores] = useState([]);

  const load = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [s, recs, st] = await Promise.all([
        api.get("/attendance/stats", { params: { target_date: today } }),
        api.get("/attendance", { params: { date_from: today, date_to: today } }),
        api.get("/stores"),
      ]);
      setStats(s.data);
      setTodayRecords(recs.data);
      setStores(st.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!stats) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="px-4 md:px-10 py-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">{user.role === "supervisor" ? "Team overview" : "Operations overview"}</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Today</h1>
          <p className="text-zinc-600 mt-1 text-sm font-mono">{stats.date}</p>
        </div>
        <button onClick={load} data-testid="refresh-dashboard-btn" className="px-4 py-2 border border-zinc-300 rounded-sm text-sm hover:bg-zinc-50">Refresh</button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile icon={Users} label="Total Staff" value={stats.total_employees} tone="default" tid="stat-total" />
        <Tile icon={CheckCircle} label="Present Now" value={stats.present_now} tone="emerald" tid="stat-present" />
        <Tile icon={Warning} label="Late" value={stats.late} tone="amber" tid="stat-late" />
        <Tile icon={XCircle} label="Absent" value={stats.absent} tone="red" tid="stat-absent" />
        <Tile icon={ClockClockwise} label="On Time" value={stats.on_time} tone="emerald" tid="stat-ontime" />
        <Tile icon={ChartPieSlice} label="Overtime" value={stats.overtime} tone="klein" tid="stat-overtime" />
        <Tile icon={CheckCircle} label="Clocked Out" value={stats.clocked_out} tone="default" tid="stat-clockedout" />
        <Tile icon={Warning} label="Left Early" value={stats.left_early} tone="orange" tid="stat-leftearly" />
      </div>

      {/* Trend chart */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">7-day trend</div>
              <h3 className="font-heading font-bold text-xl text-zinc-950 mt-1">Attendance by status</h3>
            </div>
          </div>
          <div className="h-64" data-testid="trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.week_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#71717a" }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="on_time" fill="#10B981" name="On Time" stackId="a" />
                <Bar dataKey="late" fill="#F59E0B" name="Late" stackId="a" />
                <Bar dataKey="absent" fill="#DC2626" name="Absent" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="label-eyebrow">Distribution</div>
          <h3 className="font-heading font-bold text-xl text-zinc-950 mt-1 mb-4">Status mix</h3>
          <div className="space-y-3">
            <DistBar label="On Time" count={stats.on_time} total={stats.total_employees} color="bg-emerald-500" />
            <DistBar label="Late" count={stats.late} total={stats.total_employees} color="bg-amber-500" />
            <DistBar label="Absent" count={stats.absent} total={stats.total_employees} color="bg-red-500" />
            <DistBar label="Overtime" count={stats.overtime} total={stats.total_employees} color="bg-klein" />
            <DistBar label="Left Early" count={stats.left_early} total={stats.total_employees} color="bg-orange-500" />
          </div>
        </div>
      </div>

      {/* Live map */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <div className="label-eyebrow">Geography</div>
            <h3 className="font-heading font-bold text-xl text-zinc-950">Stores & live clock-ins</h3>
          </div>
          <div className="text-xs font-mono text-zinc-500">{stores.length} stores · {todayRecords.length} pins</div>
        </div>
        <LiveMap stores={stores} records={todayRecords} height={380} />
      </div>

      {/* Live roster */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <div className="label-eyebrow">Live roster</div>
            <h3 className="font-heading font-bold text-xl text-zinc-950">Today's attendance</h3>
          </div>
          <div className="text-xs font-mono text-zinc-500">{todayRecords.length} records</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>Employee</Th>
              <Th>Store</Th>
              <Th>Status</Th>
              <Th className="text-right">Clock In</Th>
              <Th className="text-right">Clock Out</Th>
              <Th className="text-right">Worked</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {todayRecords.length === 0 && (
              <tr><td colSpan={6} className="text-center text-zinc-500 py-10">No clock-ins yet today</td></tr>
            )}
            {todayRecords.map((r) => (
              <tr key={r.id} data-testid={`roster-row-${r.id}`}>
                <Td>
                  <div className="font-semibold text-zinc-950">{r.user_name}</div>
                  <div className="text-xs text-zinc-500">{r.user_email}</div>
                </Td>
                <Td>{r.store_name || "—"}</Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td className="text-right font-mono tabular-nums">{fmt(r.clock_in_at)}</Td>
                <Td className="text-right font-mono tabular-nums">{fmt(r.clock_out_at)}</Td>
                <Td className="text-right font-mono tabular-nums">
                  {r.worked_minutes ? `${Math.floor(r.worked_minutes/60)}h ${r.worked_minutes%60}m` : "—"}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const tones = {
  default: "bg-white border-zinc-200",
  emerald: "bg-emerald-50 border-emerald-200",
  amber: "bg-amber-50 border-amber-200",
  red: "bg-red-50 border-red-200",
  klein: "bg-blue-50 border-blue-200",
  orange: "bg-orange-50 border-orange-200",
};
const toneIcon = {
  default: "text-zinc-700",
  emerald: "text-emerald-700",
  amber: "text-amber-700",
  red: "text-red-700",
  klein: "text-klein",
  orange: "text-orange-700",
};

function Tile({ icon: Icon, label, value, tone, tid }) {
  return (
    <div className={`border rounded-sm p-5 ${tones[tone] || tones.default}`} data-testid={tid}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">{label}</div>
        <Icon size={18} weight="duotone" className={toneIcon[tone] || toneIcon.default} />
      </div>
      <div className="font-heading font-black text-4xl tracking-tighter text-zinc-950 tabular-nums">{value}</div>
    </div>
  );
}

function DistBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-zinc-700 font-medium">{label}</span>
        <span className="font-mono text-zinc-500">{count} · {pct}%</span>
      </div>
      <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-zinc-900 ${className}`}>{children}</td>
);
